// Public crew profile (PROTO profile.html / profile_page.py's PROFILE_BODY +
// PROFILE_CSS), ported faithfully: the member on the black cloth banner,
// their favourite ship and flagship name, their medals/ribbons/ensigns and
// collection progress. Decoration art renders when an accomplishment has
// one; otherwise the hatched "art" placeholder from the mock. Signed-out
// visitors can view this page same as anyone else — it carries no session
// data (see buildCrewProfile's own key-set tests on the API side).
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import type { EarnedDecoration } from '@clan-heart/shared';
import { shipById, shipLengthScale } from '@clan-heart/shared';
import { clickShip, getCrewProfile, reportBroadside } from '../../api.js';
import { BannerCanvas } from '../BannerCanvas.js';
import { Opener } from '../Opener.js';
import { reduceMotion } from '../motion.js';
import {
  DEFAULT_HULL,
  NEW_SHIP_AFTER_SECONDS,
  RELOAD_SECONDS,
  SINK_SECONDS,
  broadsideGuns,
  hullOfImage,
  playBroadside,
  playSink,
  type HullBox,
} from '../broadside.js';
import { splashScreen } from '../drops.js';
import { startStorm } from '../storm.js';
import { SmokeLayer } from '../smoke.js';
import {
  canonicalRedirectPath,
  deriveProfileDisplay,
  monthYear,
  decorationDetails,
  starIcons,
  yearOf,
} from './profileDisplay.js';
import './ProfilePage.css';

function barWidth(numerator: number, denominator: number): CSSProperties {
  const pct = denominator <= 0 ? 0 : Math.max(0, Math.min(100, Math.round((numerator * 100) / denominator)));
  return { width: `${pct}%` };
}

/** A medal/ribbon/ensign's art slot: the uploaded image when `art` is set,
 * else the dashed hatched placeholder from the mock. `locked` dims it for
 * the "closest to earning" list, which shows accomplishments not yet fully
 * earned. */
function DecorationSlot({
  art,
  sizeClass,
  locked,
  children,
}: {
  art: string | null;
  sizeClass: string;
  locked?: boolean;
  children?: ReactNode;
}) {
  const classes = [sizeClass, locked ? 'ph-locked' : ''].filter(Boolean).join(' ');
  if (art) {
    return (
      <span className={`art ${classes}`}>
        <img src={art} alt="" loading="lazy" />
        {children}
      </span>
    );
  }
  return (
    <span className={`ph ${classes}`} aria-hidden={children ? undefined : true}>
      {children}
    </span>
  );
}

function MedalItem({ medal }: { medal: EarnedDecoration }) {
  const tipId = `md-tip-${medal.accomplishmentId}`;
  return (
    <li className="md" tabIndex={0} aria-describedby={tipId}>
      <DecorationSlot art={medal.art} sizeClass="ph-medal" />
      <div>
        <p className="md-name">{medal.title}</p>
        <p className="md-when">Awarded {monthYear(medal.firstEarnedAt)}</p>
        <p className="md-why">{medal.description}</p>
      </div>
      <DecorationTip id={tipId} decoration={medal} />
    </li>
  );
}

/** The hover/focus card shared by ribbons, medals and ensigns. */
function DecorationTip({ id, decoration }: { id: string; decoration: EarnedDecoration }) {
  return (
    <span className="deco-tip" role="tooltip" id={id}>
      <span className="deco-tip-title">{decoration.title}</span>
      {decorationDetails(decoration).map((line) => (
        <span key={line} className="deco-tip-line">
          {line}
        </span>
      ))}
    </span>
  );
}

/** Every ship is drawn to one scale, measured against the Pirate Frigate,
 * which keeps its size (432px wide at most); the rest follow their length. */
const REFERENCE_SHIP = 'pirate-frigate';

/** Pure: a ship's drawn size next to the reference ship (1). */
function shipSize(id: string): number {
  const ship = shipById(id);
  const reference = shipById(REFERENCE_SHIP);
  if (!ship || !reference) return 1;
  return shipLengthScale(ship) / shipLengthScale(reference);
}

/** Top speed of the sailing ship, in CSS pixels a second — a slow glide it never exceeds. */
const SHIP_SPEED = 45;
/** How quickly it gathers way, in px/s per second — a ship never surges. */
const SHIP_ACCELERATION = 18;
/** It comes into view already underway, at this share of its top speed. */
const SHIP_ENTRY_SPEED = 0.5;

/** The beloved ship's one voyage per page view. It waits with its bow at the
 * right edge of the view; as soon as its area comes into view it sails in,
 * already underway, gathers way slowly (never past SHIP_SPEED), eases off near its berth and stays
 * there — it never sails back out, whatever the scroll does. Only a fresh
 * page load starts a new voyage. If the area is already scrolled past when
 * the page opens (the browser restoring the scroll), it is simply moored.
 * Moored, it drifts slowly back and forth a few pixels (see .pf-voyage-drift).
 * Under reduced motion it is always moored. */
function SailingShip({
  slug,
  size,
  src,
  guns,
  decks,
}: {
  slug: string;
  size: number;
  src: string;
  guns: number;
  decks: number;
}) {
  const slotRef = useRef<HTMLDivElement>(null);
  const hullRef = useRef<HTMLDivElement>(null);
  const smokeRef = useRef<HTMLCanvasElement>(null);
  // sends a new ship in from the right edge (after the last one sank)
  const relaunchRef = useRef<() => void>(() => {});
  useEffect(() => {
    const slot = slotRef.current;
    const hull = hullRef.current;
    if (!slot || !hull || reduceMotion) return;
    // where the ship waits: its bow (the left end) right at the edge of the view
    const berthLeft = hull.getBoundingClientRect().left;
    let x = Math.max(0, window.innerWidth - berthLeft);
    let v = SHIP_SPEED * SHIP_ENTRY_SPEED; // current speed, px/s
    let state: 'waiting' | 'sailing' | 'moored' = 'waiting';
    let frame = 0;
    let last = 0;
    const place = () => {
      hull.style.transform = x === 0 ? '' : `translateX(${x.toFixed(1)}px)`;
    };
    const sail = (now: number) => {
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60;
      last = now;
      // gather way gently, never past top speed, and ease off over the last stretch
      const cruise = Math.min(SHIP_SPEED, x * 0.6 + 4);
      v = Math.min(cruise, v + SHIP_ACCELERATION * dt);
      x = x <= v * dt ? 0 : x - v * dt;
      place();
      if (x === 0) {
        state = 'moored';
        hull.classList.add('is-moored');
        frame = 0;
      } else {
        frame = requestAnimationFrame(sail);
      }
    };
    const look = () => {
      if (state !== 'waiting') return;
      const r = slot.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      if (r.bottom < 0) {
        // already scrolled past (e.g. a restored scroll position): it is simply there
        x = 0;
        state = 'moored';
        hull.classList.add('is-moored');
        place();
      } else if (r.top < vh * 0.9) {
        state = 'sailing';
        frame = requestAnimationFrame(sail);
      }
    };
    relaunchRef.current = () => {
      if (frame) cancelAnimationFrame(frame);
      hull.classList.remove('is-moored');
      x = Math.max(0, window.innerWidth - (hull.getBoundingClientRect().left - x));
      v = SHIP_SPEED * SHIP_ENTRY_SPEED;
      last = 0;
      state = 'sailing';
      place();
      frame = requestAnimationFrame(sail);
    };
    place();
    look();
    window.addEventListener('scroll', look, { passive: true });
    window.addEventListener('resize', look);
    return () => {
      window.removeEventListener('scroll', look);
      window.removeEventListener('resize', look);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  return (
    // the slot stays put (it is what gets measured); the hull inside it sails
    <div ref={slotRef} className="pf-voyage-ship">
      {/* the powder smoke hangs here, around the ship, and does not sail with it */}
      <canvas ref={smokeRef} className="pf-smoke-canvas" aria-hidden="true" />
      <div
        ref={hullRef}
        className={reduceMotion ? 'pf-voyage-hull is-moored' : 'pf-voyage-hull'}
        style={{ '--ship-size': size.toFixed(3) } as CSSProperties}
      >
        {/* once moored it drifts a little back and forth; the image itself rocks on the swell */}
        <div className="pf-voyage-drift">
          <Broadside slug={slug} guns={guns} decks={decks} size={size} smoke={smokeRef} relaunch={relaunchRef}>
            <img className="pf-sail-ship" src={src} alt="" />
          </Broadside>
        </div>
      </div>
    </div>
  );
}

/** Gives the smoke canvas the whole width of the ship's area and room to rise above it. */
function placeSmoke(canvas: HTMLCanvasElement): void {
  const slot = canvas.parentElement;
  const section = canvas.closest('.pf-voyage');
  if (!slot || !section) return;
  const s = slot.getBoundingClientRect();
  const v = section.getBoundingClientRect();
  const above = window.innerHeight * 0.6;
  canvas.style.left = `${v.left - s.left}px`;
  canvas.style.top = `${v.top - s.top - above}px`;
  canvas.style.width = `${v.width}px`;
  canvas.style.height = `${v.height + above}px`;
}

/** A hidden gimmick: click (or tap) the ship to fire a broadside — no
 * tooltip, no pointer, not in the tab order. The guns flash on the hull from
 * bow to stern with a cannon shot each, the ship shudders, and the powder
 * smoke is drawn on `smoke` (a canvas around the ship, which does not sail
 * with it). Then the guns reload for RELOAD_SECONDS, shown as a small ring
 * that fills up. Every 5th click on this ship (counted on the server, all
 * visitors together, also clicks while reloading) brings a squall over it
 * (storm.ts) as it fires; every 12th sinks it instead: its
 * stern catches fire, then it goes down by the stern with the sea
 * pouring in, water splashes over the whole screen, and after
 * NEW_SHIP_AFTER_SECONDS a new ship sails in from the right. */
function Broadside({
  slug,
  guns,
  decks,
  size,
  smoke,
  relaunch,
  children,
}: {
  slug: string;
  guns: number;
  decks: number;
  size: number;
  smoke: RefObject<HTMLCanvasElement | null>;
  relaunch: RefObject<() => void>;
  children: ReactNode;
}) {
  const [firing, setFiring] = useState(0);
  const [reloading, setReloading] = useState(false);
  const [sinking, setSinking] = useState(false);
  // one question to the server at a time, so a burst of clicks doesn't queue up
  const askingRef = useRef(false);
  useEffect(() => {
    if (!sinking) return;
    const done = window.setTimeout(() => {
      setSinking(false);
      setReloading(false);
      relaunch.current();
    }, NEW_SHIP_AFTER_SECONDS * 1000);
    return () => window.clearTimeout(done);
  }, [sinking, relaunch]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<SmokeLayer | null>(null);
  const hullRef = useRef<HullBox | null>(null);
  useEffect(() => () => layerRef.current?.stop(), []);
  useEffect(() => {
    if (!firing) return;
    const done = window.setTimeout(() => setFiring(0), 1900);
    return () => window.clearTimeout(done);
  }, [firing]);
  useEffect(() => {
    if (!reloading) return;
    const done = window.setTimeout(() => setReloading(false), RELOAD_SECONDS * 1000);
    return () => window.clearTimeout(done);
  }, [reloading]);

  async function click() {
    if (sinking || askingRef.current) return;
    askingRef.current = true;
    try {
      const action = await clickShip(slug);
      if (action === 'sink') {
        sink();
        return;
      }
      if (action === 'storm') {
        const area = wrapRef.current?.closest<HTMLElement>('.pf-voyage');
        if (area) void startStorm(area);
      }
      fire();
    } finally {
      askingRef.current = false;
    }
  }

  function sink() {
    setSinking(true);
    playSink();
    if (!reduceMotion) void splashScreen(SINK_SECONDS);
    // the stern catches fire: dark smoke, thickening, that goes down with the ship
    const img = wrapRef.current?.querySelector('img');
    const canvas = smoke.current;
    if (!img || !canvas) return;
    hullRef.current ??= hullOfImage(img) ?? DEFAULT_HULL;
    const hull = hullRef.current;
    placeSmoke(canvas);
    layerRef.current ??= new SmokeLayer(canvas, reduceMotion);
    const sea = canvas.closest('.pf-voyage')?.querySelector('.pf-sail-sea');
    layerRef.current.burn(
      () => {
        const box = img.getBoundingClientRect();
        const area = canvas.getBoundingClientRect();
        // the ocean's surface: a little way down its banner (the waves rise and fall around it)
        const surface = sea ? sea.getBoundingClientRect() : null;
        const waterline = surface ? surface.top - area.top + surface.height * 0.12 : Infinity;
        const x = box.left - area.left + (hull.left + (hull.right - hull.left) * 0.88) * box.width;
        const y = box.top - area.top + (hull.top - 0.01) * box.height;
        // once the stern reaches the water, the fire is out
        return y >= waterline - 2 ? null : { x, y, waterline };
      },
      size,
      SINK_SECONDS * 0.7,
    );
  }

  function fire() {
    const img = wrapRef.current?.querySelector('img');
    const canvas = smoke.current;
    if (reloading || !img || !canvas) return;
    hullRef.current ??= hullOfImage(img) ?? DEFAULT_HULL;
    const volley = broadsideGuns(guns, decks, hullRef.current);
    placeSmoke(canvas);
    const box = img.getBoundingClientRect();
    const area = canvas.getBoundingClientRect();
    layerRef.current ??= new SmokeLayer(canvas, reduceMotion);
    layerRef.current.fire(
      volley.map((gun) => ({ x: box.left - area.left + gun.x * box.width, y: box.top - area.top + gun.y * box.height, delay: gun.delay })),
      size,
    );
    playBroadside(volley);
    // tallied for the cave leaderboard (the server counts one per visitor per reload)
    reportBroadside(slug);
    setFiring(Date.now());
    setReloading(true);
  }

  return (
    <div ref={wrapRef} className={sinking ? 'pf-broadside is-sinking' : firing ? 'pf-broadside is-firing' : 'pf-broadside'}>
      <button
        type="button"
        className="pf-fire"
        tabIndex={-1}
        aria-hidden="true"
        onClick={click}
        style={{ '--sink-seconds': `${SINK_SECONDS}s` } as CSSProperties}
      >
        {children}
      </button>
      {reloading && !sinking && (
        <svg className="pf-reload" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="pf-reload-track" cx="12" cy="12" r="9" />
          <circle className="pf-reload-fill" cx="12" cy="12" r="9" style={{ animationDuration: `${RELOAD_SECONDS}s` }} />
        </svg>
      )}
    </div>
  );
}

function RibbonItem({ ribbon }: { ribbon: EarnedDecoration }) {
  const icons = starIcons(ribbon.stars.tier);
  const tipId = `rb-tip-${ribbon.accomplishmentId}`;
  // focusable, so the card also opens from the keyboard, not only on hover
  return (
    <li className="rb" tabIndex={0} aria-describedby={tipId}>
      <DecorationSlot art={ribbon.art} sizeClass="ph-ribbon">
        {icons.map((modifier, i) => (
          <i key={i} className={`st st-${modifier}`} aria-hidden="true" />
        ))}
      </DecorationSlot>
      <span className="rb-name">{ribbon.title}</span>
      <DecorationTip id={tipId} decoration={ribbon} />
    </li>
  );
}

function EnsignItem({ ensign }: { ensign: EarnedDecoration }) {
  const tipId = `en-tip-${ensign.accomplishmentId}`;
  // like a medal: what it was for, clamped, with the full story in the hover card
  return (
    <li className="en" tabIndex={0} aria-describedby={tipId}>
      <DecorationSlot art={ensign.art} sizeClass="ph-ensign" />
      <p className="en-name">{ensign.title}</p>
      <p className="en-year">{yearOf(ensign.firstEarnedAt)}</p>
      {ensign.description && <p className="en-why">{ensign.description}</p>}
      <DecorationTip id={tipId} decoration={ensign} />
    </li>
  );
}

export function ProfilePage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ['crew', slug], queryFn: () => getCrewProfile(slug) });

  // the layout titles pages by path; a profile is titled by its sailor once loaded
  const name = query.data?.name;
  useEffect(() => {
    if (name) document.title = `${name} · Clan Heart`;
  }, [name]);

  // An old link keeps working: a rename moves the canonical slug, so once
  // loaded we quietly replace the URL rather than show a stale one.
  useEffect(() => {
    if (!query.data) return;
    const target = canonicalRedirectPath(slug, query.data);
    if (target) navigate(target, { replace: true });
  }, [query.data, slug, navigate]);

  if (query.isLoading) {
    return (
      <section className="wrap section">
        <p>Loading…</p>
      </section>
    );
  }

  if (query.isError) {
    return (
      <section className="wrap section">
        <p className="prose big">{(query.error as Error).message}</p>
      </section>
    );
  }

  const profile = query.data ?? null;

  if (!profile) {
    return (
      <>
        <Opener lines={['Lost at', 'sea']} />
        <section className="wrap section">
          <p className="prose big">
            No crew member sails under that name. <Link to="/about">Back to the Hall of Members</Link>
          </p>
        </section>
      </>
    );
  }

  const display = deriveProfileDisplay(profile);

  return (
    <>
      <section className="pf-hero" aria-label={profile.name}>
        <BannerCanvas kind="cloth" />
        <div className="wrap pf-hero-in">
          <div className="pf-id">
            <span className="label pf-rank">{profile.rank}</span>
            <h1 className="pf-name">{profile.name}</h1>
            {display.motto && <p className="pf-motto">{display.motto}</p>}
            <p className="pf-since">{display.sinceLabel}</p>
            <dl className="pf-stats">
              <div>
                <dt>Decorations</dt>
                <dd>{profile.stats.decorations}</dd>
              </div>
              <div>
                <dt>Points</dt>
                <dd>{profile.stats.points}</dd>
              </div>
              <div>
                <dt>Leaderboard</dt>
                <dd>{display.leaderboardLabel}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* the member's ship, flagship and colours in one area; with a plan
          picture the ship sails in over the ocean banner. The flag stays on
          parchment — never on the black banner. */}
      <section
        className={display.ship?.planImage ? 'pf-voyage pf-voyage--afloat' : 'pf-voyage'}
        aria-label="Ship, flagship and colours"
      >
        <div className="wrap pf-voyage-in">
          <div className="pf-voyage-text">
            <div className="pf-ship-col">
              <span className="label">Most beloved ship</span>
              {display.ship ? (
                <>
                  <p className="pf-ship-name">{display.ship.name}</p>
                  <p className="pf-ship-meta">{display.ship.meta}</p>
                </>
              ) : (
                <p className="pf-ship-meta">No ship chosen yet</p>
              )}
            </div>
            <div className="pf-ship-col pf-colours">
              <img className="pf-flag" src={profile.flag} alt={`${profile.name}'s flag`} />
              <div>
                <span className="label">{display.flagshipQuote ? 'Flagship' : 'Colours'}</span>
                {display.flagshipQuote && <p className="pf-flagship">{display.flagshipQuote}</p>}
              </div>
            </div>
          </div>
          {display.ship?.planImage && profile.favouriteShip && (
            <SailingShip
              slug={profile.slug}
              size={shipSize(profile.favouriteShip.id)}
              src={display.ship.planImage}
              guns={profile.favouriteShip.guns}
              decks={profile.favouriteShip.decks}
            />
          )}
        </div>
        {display.ship?.planImage && (
          <div className="pf-sail-sea" aria-hidden="true">
            <BannerCanvas kind="sea" />
          </div>
        )}
      </section>

      {display.showMedals && (
        <section className="wrap pf-sec" aria-labelledby="medals-h">
          <header className="pf-sec-head">
            <h2 className="h2" id="medals-h">
              Medals
            </h2>
            <span className="pf-count">{display.medalsSummary}</span>
          </header>
          <ul className="pf-medals">
            {profile.medals.map((m) => (
              <MedalItem key={m.accomplishmentId} medal={m} />
            ))}
          </ul>
        </section>
      )}

      {display.showRibbons && (
        <section className="wrap pf-sec" aria-labelledby="ribbons-h">
          <header className="pf-sec-head">
            <h2 className="h2" id="ribbons-h">
              Ribbons
            </h2>
            <span className="pf-count">{display.ribbonsSummary}</span>
          </header>
          <ul className="pf-rack">
            {profile.ribbons.map((r) => (
              <RibbonItem key={r.accomplishmentId} ribbon={r} />
            ))}
          </ul>
          {display.showServiceStarNote && (
            <p className="pf-note">
              Service stars mark repeat accomplishments: bronze, silver and gold at thresholds the officers set.
            </p>
          )}
        </section>
      )}

      {display.showEnsigns && (
        <section className="wrap pf-sec" aria-labelledby="ensigns-h">
          <header className="pf-sec-head">
            <h2 className="h2" id="ensigns-h">
              Ensigns sailed under
            </h2>
            <span className="pf-count">{display.ensignsSummary}</span>
          </header>
          <ul className="pf-ensigns">
            {profile.ensigns.map((e) => (
              <EnsignItem key={e.accomplishmentId} ensign={e} />
            ))}
          </ul>
        </section>
      )}

      {display.showCollection && (
        <section className="wrap pf-sec" aria-labelledby="coll-h">
          <header className="pf-sec-head">
            <h2 className="h2" id="coll-h">
              The collection
            </h2>
            <span className="pf-count">{display.collectionSummary}</span>
          </header>
          <div className="pf-coll">
            <ul className="pf-progress">
              {profile.collection.categories.map((c) => (
                <li key={c.category}>
                  <span className="pg-name">{c.label}</span>
                  <span className="pg-bar">
                    <i style={barWidth(c.earned, c.total)} />
                  </span>
                  <span className="pg-num">
                    {c.earned} / {c.total}
                  </span>
                </li>
              ))}
            </ul>
            {display.showClosest && (
              <div className="pf-next">
                <span className="label">Closest to earning</span>
                <ul>
                  {profile.closestToEarning.map((item) => (
                    <li key={item.accomplishmentId}>
                      <DecorationSlot art={item.art} sizeClass="ph-ribbon" locked />
                      <div>
                        <p className="nx-name">{item.title}</p>
                        <p className="nx-how">{item.description}</p>
                        <span className="pg-bar">
                          <i style={barWidth(item.count, item.next)} />
                        </span>
                        <span className="pg-num">
                          {item.count} / {item.next}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}
